import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import type { ReactNode } from 'react';
import type { ViewStyle } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { ChatCustomView, type CustomBubbleProps } from '@/components/chat';
import { ChatBubble, WEB_CAPTION_PAIR_MAX_WIDTH } from '@/components/chat/message-list/ChatBubble';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { useColors } from '@/hooks/useColors';

type Colors = ReturnType<typeof useColors>['colors'];

/**
 * Web: the caption column shrink-wraps its bubble up to the shared pair cap. A fixed pixel cap
 * overruns a pane narrower than it, and a right-aligned column then spills past the pane's left
 * edge, where it is clipped. Set inline because NativeWind does not compile an arbitrary `min`
 * max-width class.
 */
const WEB_CAPTION_COLUMN_STYLE = { maxWidth: WEB_CAPTION_PAIR_MAX_WIDTH } as unknown as ViewStyle;

/**
 * Caption line plus bubble for expert-relay, Ask Anonymously routing, and support copy.
 * Aligns caption and bubble on web/native; indents caption with the bubble for relay-as-viewer
 * unless the viewer is the inquiry author. VStack uses `min-w-0 items-start` for flex shrink.
 *
 * `bubbleSlot` lets the caller swap the default text bubble for a richer bubble
 * variant (e.g. `ExtractedStatementsBubble`) so captioned messages — including
 * expert relays — still surface extracted statements under the caption line.
 * When omitted, the default `ChatBubble` renders.
 */
export function MessageBubbleWithLabel({
  messageCaption,
  shouldLeftAlignAskAnonymouslyCaption,
  isCurrentUserMessage,
  isOutgoingCaptionWeb,
  isExpertRelayMessage,
  bubbleProps,
  colors,
  closedConversation,
  isAuthor,
  conversationId,
  userId,
  bubbleSlot,
}: {
  messageCaption: string;
  shouldLeftAlignAskAnonymouslyCaption: boolean;
  isCurrentUserMessage: boolean;
  isOutgoingCaptionWeb: boolean;
  isExpertRelayMessage: boolean;
  bubbleProps: CustomBubbleProps;
  colors: Colors;
  closedConversation: boolean;
  isAuthor: boolean;
  conversationId: string;
  userId: string;
  bubbleSlot?: ReactNode;
}) {
  const applyExpertRelayViewerCaptionInset =
    isCurrentUserMessage &&
    isExpertRelayMessage &&
    shouldLeftAlignAskAnonymouslyCaption &&
    !isAuthor;

  const captionTextCore = applyExpertRelayViewerCaptionInset ? 'mb-1 ml-11' : 'mb-1';

  const webShrinkCaptionColumn =
    isWeb &&
    ((shouldLeftAlignAskAnonymouslyCaption && !isCurrentUserMessage) || isOutgoingCaptionWeb) &&
    'web:inline-flex web:flex-col';

  return (
    <VStack
      className={twJoin(
        'min-w-0 items-start',
        !isCurrentUserMessage && 'self-start',
        isCurrentUserMessage && !isOutgoingCaptionWeb && 'self-end',
        webShrinkCaptionColumn,
        isOutgoingCaptionWeb && 'web:self-end'
      )}
      style={webShrinkCaptionColumn ? WEB_CAPTION_COLUMN_STYLE : undefined}>
      <Text
        size="xs"
        className={twJoin(
          captionTextCore,
          shouldLeftAlignAskAnonymouslyCaption && !isCurrentUserMessage ? 'pl-0' : 'pl-4',
          isCurrentUserMessage && !shouldLeftAlignAskAnonymouslyCaption ? 'text-right' : 'text-left'
        )}>
        {messageCaption}
      </Text>
      {bubbleSlot ?? (
        <ChatBubble
          shrinkWidthToContent
          bubbleProps={bubbleProps}
          colors={colors}
          renderCustomContent={(customViewProps) => {
            const customBubbleProps = customViewProps as CustomBubbleProps;

            return (
              <ChatCustomView
                {...customBubbleProps}
                closedConversation={closedConversation}
                isAuthor={isAuthor}
                conversationId={conversationId}
                userId={userId}
              />
            );
          }}
        />
      )}
    </VStack>
  );
}
